import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";

// Schema for a test history run
const testHistoryRunSchema = z.object({
  timestamp: z.string(),
  status: z.enum(["pass", "fail", "error", "skip"]),
  duration_seconds: z.number().nullable(),
  branch: z.string().nullable(),
  sha: z.string().nullable(),
  commit_message: z.string().nullable(),
  pr_number: z.number().nullable(),
  run_id: z.string().nullable(),
  job_id: z.string().nullable(),
  test_id: z.string().nullable().optional(),
  // Optional for backward compatibility
  logs: z.string().nullable(),
  user: z.string().nullable().optional(),
  // User who triggered the run
  similarity_score: z.number().nullable().optional() // Similarity score (0-1) when include_similarity is true
});

// Schema for the test history response
const testHistoryResponseSchema = z.object({
  runs: z.array(testHistoryRunSchema),
  total_count: z.number()
});
export type TestHistoryRun = z.infer<typeof testHistoryRunSchema>;
export type TestHistoryResponse = z.infer<typeof testHistoryResponseSchema>;
interface UseTestHistoryParams {
  org: string;
  repository: string;
  testSuite: string | null;
  testName: string;
  testType: "junit" | "auto";
  enabled?: boolean;
  timeRangeHours?: number; // Lookback period in hours (3, 12, 24, 48, 120, 336)
  branchFilter?: "all" | "main" | "current" | "main_and_current" | "latest_per_branch";
  currentBranch?: string; // Required when branchFilter is "current" or "main_and_current"
  testId?: string; // The test ID for the reference test (used for similarity comparison)
  includeSimilarity?: boolean; // Whether to include similarity scores in the response
}
export function useTestHistory(t0) {
  const $ = _c(41);
  const {
    org,
    repository,
    testSuite,
    testName,
    testType,
    enabled: t1,
    timeRangeHours: t2,
    branchFilter: t3,
    currentBranch,
    testId,
    includeSimilarity: t4
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const timeRangeHours = t2 === undefined ? 72 : t2;
  const branchFilter = t3 === undefined ? "all" : t3;
  const includeSimilarity = t4 === undefined ? false : t4;
  const api = useApi();
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Date();
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const endDate = t5;
  let startDate;
  if ($[1] !== timeRangeHours) {
    startDate = new Date();
    startDate.setHours(startDate.getHours() - timeRangeHours);
    $[1] = timeRangeHours;
    $[2] = startDate;
  } else {
    startDate = $[2];
  }
  let t6;
  if ($[3] !== branchFilter || $[4] !== currentBranch || $[5] !== includeSimilarity || $[6] !== org || $[7] !== repository || $[8] !== testId || $[9] !== testName || $[10] !== testSuite || $[11] !== testType || $[12] !== timeRangeHours) {
    t6 = ["testHistory", org, repository, testSuite, testName, testType, timeRangeHours, branchFilter, currentBranch, testId, includeSimilarity];
    $[3] = branchFilter;
    $[4] = currentBranch;
    $[5] = includeSimilarity;
    $[6] = org;
    $[7] = repository;
    $[8] = testId;
    $[9] = testName;
    $[10] = testSuite;
    $[11] = testType;
    $[12] = timeRangeHours;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== api || $[15] !== branchFilter || $[16] !== currentBranch || $[17] !== includeSimilarity || $[18] !== org || $[19] !== repository || $[20] !== startDate || $[21] !== testId || $[22] !== testName || $[23] !== testSuite || $[24] !== testType) {
    t7 = async () => {
      if (!org || !repository || !testName) {
        return null;
      }
      if ((branchFilter === "current" || branchFilter === "main_and_current") && (!currentBranch || typeof currentBranch !== "string" || currentBranch.trim() === "")) {
        console.warn(`[useTestHistory] Cannot fetch test history: branchFilter is "${branchFilter}" but currentBranch is not available`, {
          branchFilter,
          currentBranch,
          org,
          repository,
          testSuite,
          testName
        });
        return null;
      }
      const params = {
        test_suite: testSuite,
        test_name: testName,
        test_type: testType,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        branch_filter: branchFilter,
        repository,
        limit: 1000
      };
      if (includeSimilarity && testId) {
        params.test_id = testId;
        params.include_similarity = 1;
      }
      if ((branchFilter === "current" || branchFilter === "main_and_current") && currentBranch) {
        params.current_branch = currentBranch;
      }
      const response = await api.get(`user/github/orgs/${org}/metrics/actions/tests/history`, {
        params
      });
      return testHistoryResponseSchema.parse(response.data);
    };
    $[14] = api;
    $[15] = branchFilter;
    $[16] = currentBranch;
    $[17] = includeSimilarity;
    $[18] = org;
    $[19] = repository;
    $[20] = startDate;
    $[21] = testId;
    $[22] = testName;
    $[23] = testSuite;
    $[24] = testType;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== branchFilter || $[27] !== currentBranch || $[28] !== enabled || $[29] !== org || $[30] !== repository || $[31] !== testName || $[32] !== testType) {
    t8 = enabled && !!org && !!repository && !!testName && !!testType && !((branchFilter === "current" || branchFilter === "main_and_current") && (!currentBranch || typeof currentBranch !== "string" || currentBranch.trim() === ""));
    $[26] = branchFilter;
    $[27] = currentBranch;
    $[28] = enabled;
    $[29] = org;
    $[30] = repository;
    $[31] = testName;
    $[32] = testType;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== t8) {
    t9 = {
      enabled: t8,
      suspense: false,
      keepPreviousData: true,
      staleTime: 300000,
      cacheTime: 600000,
      retry: _temp
    };
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const {
    data,
    isLoading,
    isFetching,
    error: error_0
  } = useQuery(t6, t7, t9);
  const t10 = data || null;
  const t11 = error_0?.response?.status === 404 ? null : error_0;
  let t12;
  if ($[36] !== isFetching || $[37] !== isLoading || $[38] !== t10 || $[39] !== t11) {
    t12 = {
      data: t10,
      isLoading,
      isFetching,
      error: t11
    };
    $[36] = isFetching;
    $[37] = isLoading;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}
function _temp(failureCount, error) {
  if (error?.response?.status === 404) {
    return false;
  }
  return failureCount < 3;
}